import { Collapse } from '@material-ui/core'
import { addTourBanner } from 'ducks/auth'
import { chatDrawerSelectors } from 'ducks/chatDrawer'
import { wsSelectors } from 'ducks/websocket'
import {
  CheckContainedIcon,
  ChevronDownOutlineIcon,
  CopyLeftOutlineIcon,
  DeleteOutlineIcon,
  HelpCircleIcon,
  Icon,
  Menu,
  MenuItem,
  SettingsIcon,
  Snackbar,
  Tooltip,
} from 'opensolar-ui'
import React, { useState } from 'react'
import { useTranslate } from 'react-admin'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from 'types/state'
import { useVoice } from 'voice/OpenAIRealtimeVoiceAPI/VoiceContext'
import GeminiLiveAPIEmbed from '../../../voice/GeminiLiveAPI/GeminiLiveAPIEmbed'
import { shouldShowLoader } from '../ChatDrawer.hooks'
import { ActionWhitelistModal } from './ActionWhitelistModal'
import { AdaChatFeedbackModal } from './AdaChatFeedbackModal'
import { copyChatTranscriptToClipboard } from './ChatTranscriptCopy'
import DockPositionSelector from './DockPositionSelector'
import { useChatSuggestionsPreferences } from './hooks/useChatSuggestionsPreferences'

type AnchorPosition = 'left' | 'top' | 'right' | 'bottom'
type DisplayMode = 'drawer' | 'dialog'

interface ChatHeaderMenuProps {
  anchorEl: HTMLElement | null
  onClose: () => void
  displayMode: DisplayMode
  anchor: AnchorPosition
  onDisplayModeChange: (mode: DisplayMode) => void
  onAnchorChange: (position: AnchorPosition) => void
  onClearChat: () => void
  enableGeminiLiveAPI?: boolean
}

export const ChatHeaderMenu: React.FC<ChatHeaderMenuProps> = ({
  anchorEl,
  onClose,
  displayMode,
  anchor,
  onDisplayModeChange,
  onAnchorChange,
  onClearChat,
  enableGeminiLiveAPI = false,
}) => {
  const dispatch = useDispatch()
  const translate = useTranslate()
  const { clearSessionState } = useVoice()
  const { tour, step } = useSelector((state: any) => state.tour)
  const isTourActive = tour === 'ada_chat' && step === 'chat_header_menu'

  const [geminiSectionExpanded, setGeminiSectionExpanded] = useState(false)
  const [advancedSectionExpanded, setAdvancedSectionExpanded] = useState(false)
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false)
  const [whitelistModalOpen, setWhitelistModalOpen] = useState(false)
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false)
  const [copyErrorSnackbarOpen, setCopyErrorSnackbarOpen] = useState(false)
  const [copyErrorMessage, setCopyErrorMessage] = useState('')
  const { preferences, setShowSuggestionsOnEmptyChat, setShowCompactSuggestions } = useChatSuggestionsPreferences()

  const messages = useSelector((state: RootState) => chatDrawerSelectors.getChatDrawerMessages(state))
  const isStreaming = useSelector(chatDrawerSelectors.getStreaming)
  const adaStatus = useSelector(chatDrawerSelectors.getAdaStatus)
  const websocketAvailable = useSelector(wsSelectors.getAvailable)

  // Calculate if suggestions are showing (chat is empty and loader is not showing)
  const showLoader = shouldShowLoader(messages, isStreaming, adaStatus, null, websocketAvailable)
  const showSuggestions = messages.length === 0 && !showLoader
  // Disable tour button if chat is not empty OR suggestions are not showing OR in full screen mode
  const isTourButtonDisabled = isTourActive || messages.length > 0 || !showSuggestions || displayMode === 'dialog'

  const getTourTooltipMessage = () => {
    if (!isTourButtonDisabled) return ''
    if (displayMode === 'dialog') {
      return translate(
        'The Ada Tour is disabled in full screen mode, please align your chat to the side of the window to begin the tour'
      )
    }
    if (messages.length > 0) {
      return translate(
        'The Ada Tour is disabled when you have an active chat session, please clear the chat to begin the tour'
      )
    }
    return ''
  }

  const handleClearChat = () => {
    onClearChat()
    clearSessionState()
    onClose()
  }

  const handleFeedbackClick = () => {
    setFeedbackModalOpen(true)
    onClose()
  }

  const handleWhitelistClick = () => {
    setWhitelistModalOpen(true)
    onClose()
  }

  const handleTakeTourClick = () => {
    dispatch(addTourBanner('ada_chat'))
    onClose()
  }

  const handleCopyTranscript = async () => {
    if (messages.length === 0) {
      setCopyErrorMessage(translate('No messages to copy'))
      setCopyErrorSnackbarOpen(true)
      onClose()
      return
    }

    const success = await copyChatTranscriptToClipboard(messages)
    if (success) {
      setCopySnackbarOpen(true)
    } else {
      setCopyErrorMessage(translate('Failed to copy transcript'))
      setCopyErrorSnackbarOpen(true)
    }
    onClose()
  }

  const handleLayoutChange = (mode: DisplayMode, position?: AnchorPosition) => {
    onDisplayModeChange(mode)
    if (position) {
      onAnchorChange(position)
    }
    onClose()
  }

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={onClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        disableScrollLock
        MenuListProps={{
          style: {
            minWidth: '200px',
            padding: 0,
          },
        }}
        PaperProps={{
          style: {
            zIndex: 2000,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)',
            borderRadius: 12,
            overflow: 'hidden',
          },
        }}
      >
        <MenuItem
          onClick={handleClearChat}
          disabled={isTourActive}
          style={{
            padding: '12px 16px',
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          <span style={{ marginRight: 12, display: 'inline-flex' }}>
            <DeleteOutlineIcon size={20} />
          </span>
          {translate('Clear Chat')}
        </MenuItem>

        <MenuItem
          onClick={handleCopyTranscript}
          disabled={isTourActive}
          style={{
            padding: '12px 16px',
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          <span style={{ marginRight: 12, display: 'inline-flex' }}>
            <CopyLeftOutlineIcon size={20} />
          </span>
          {translate('Copy Chat Transcript')}
        </MenuItem>

        <MenuItem
          onClick={handleFeedbackClick}
          disabled={isTourActive}
          style={{
            padding: '12px 16px',
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          <Icon icon="feedback" size="small" style={{ marginRight: 12, fontSize: 20 }} />
          {translate('Leave Feedback')}
        </MenuItem>

        <Tooltip title={getTourTooltipMessage()}>
          <span>
            <MenuItem
              onClick={handleTakeTourClick}
              disabled={isTourButtonDisabled}
              style={{
                padding: '12px 16px',
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              <Icon icon="explore" size="small" style={{ marginRight: 12, fontSize: 20 }} />
              {translate('Take Ada Tour')}
            </MenuItem>
          </span>
        </Tooltip>

        <div style={{ height: '1px', backgroundColor: 'rgba(0, 0, 0, 0.08)', margin: '8px 0' }} />

        <MenuItem
          onClick={() => setAdvancedSectionExpanded(!advancedSectionExpanded)}
          disabled={isTourActive}
          style={{
            padding: '12px 16px',
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          <span
            style={{
              marginRight: 12,
              display: 'inline-flex',
              transform: advancedSectionExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
            }}
          >
            <ChevronDownOutlineIcon size={20} />
          </span>
          {translate('Advanced')}
        </MenuItem>
        <Collapse in={advancedSectionExpanded} timeout="auto" unmountOnExit>
          <div
            style={{
              padding: '0 16px 8px 16px',
              backgroundColor: '#f8f9fa',
              borderTop: '1px solid rgba(0, 0, 0, 0.08)',
            }}
          >
            <MenuItem
              onClick={handleWhitelistClick}
              disabled={isTourActive}
              style={{
                padding: '12px 16px',
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              <span style={{ marginRight: 12, display: 'inline-flex' }}>
                <SettingsIcon size={20} />
              </span>
              {translate('Manage Whitelisted Actions')}
            </MenuItem>
            <MenuItem
              onClick={() => {
                setShowSuggestionsOnEmptyChat(!preferences.showSuggestionsOnEmptyChat)
              }}
              disabled={isTourActive}
              leadingIcon={
                <span style={{ marginRight: 12, display: 'inline-flex' }}>
                  <HelpCircleIcon size={20} />
                </span>
              }
              trailingIcon={
                preferences.showSuggestionsOnEmptyChat ? (
                  <CheckContainedIcon width="18px" height="18px" fill="#1976d2" />
                ) : undefined
              }
              style={{
                padding: '12px 16px',
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              <span
                style={
                  preferences.showSuggestionsOnEmptyChat
                    ? {
                        background: 'linear-gradient(90deg, #1751D0 0.01%, #3C91E6 98.75%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                      }
                    : {}
                }
              >
                {translate('Show Suggestions on Empty Chat')}
              </span>
            </MenuItem>
            <MenuItem
              onClick={() => {
                setShowCompactSuggestions(!preferences.showCompactSuggestions)
              }}
              disabled={isTourActive}
              leadingIcon={<Icon icon="view_compact" size="small" style={{ marginRight: 12, fontSize: 20 }} />}
              trailingIcon={
                preferences.showCompactSuggestions ? (
                  <CheckContainedIcon width="18px" height="18px" fill="#1976d2" />
                ) : undefined
              }
              style={{
                padding: '12px 16px',
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              <span
                style={
                  preferences.showCompactSuggestions
                    ? {
                        background: 'linear-gradient(90deg, #1751D0 0.01%, #3C91E6 98.75%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                      }
                    : {}
                }
              >
                {translate('Show Compact Suggestions')}
              </span>
            </MenuItem>
          </div>
        </Collapse>

        <div style={{ height: '1px', backgroundColor: 'rgba(0, 0, 0, 0.08)', margin: '8px 0' }} />

        <DockPositionSelector
          currentAnchor={anchor}
          currentDisplayMode={displayMode}
          onLayoutChange={handleLayoutChange}
          disabled={isTourActive}
        />

        {enableGeminiLiveAPI && (
          <>
            <div style={{ height: '1px', backgroundColor: 'rgba(0, 0, 0, 0.08)', margin: '8px 0' }} />
            <MenuItem
              onClick={() => setGeminiSectionExpanded(!geminiSectionExpanded)}
              disabled={isTourActive}
              style={{
                padding: '12px 16px',
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              <span
                style={{
                  marginRight: 12,
                  display: 'inline-flex',
                  transform: geminiSectionExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                }}
              >
                <ChevronDownOutlineIcon size={20} />
              </span>
              Gemini Live API
            </MenuItem>
            <Collapse in={geminiSectionExpanded} timeout="auto" unmountOnExit>
              <div
                style={{
                  padding: '16px',
                  backgroundColor: '#f8f9fa',
                  borderTop: '1px solid rgba(0, 0, 0, 0.08)',
                }}
              >
                <GeminiLiveAPIEmbed />
              </div>
            </Collapse>
          </>
        )}
      </Menu>

      <AdaChatFeedbackModal open={feedbackModalOpen} onClose={() => setFeedbackModalOpen(false)} />
      <ActionWhitelistModal open={whitelistModalOpen} onClose={() => setWhitelistModalOpen(false)} />

      <Snackbar
        open={copySnackbarOpen}
        autoHideDuration={3000}
        onClose={() => setCopySnackbarOpen(false)}
        message={translate('Chat transcript copied to clipboard')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      <Snackbar
        open={copyErrorSnackbarOpen}
        autoHideDuration={3000}
        onClose={() => setCopyErrorSnackbarOpen(false)}
        message={copyErrorMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  )
}
